import React, { useState, useEffect, useContext } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import Title from '../components/Title';
import SubmitButton from '../components/SubmitButton';
import useGtmEventTracking from '../components/useGtmEventTracking';
import UserContext from '../contexts/UserContext';
import { useFlow } from '../utilities/useFlow';
import { axiosKK } from '../services/networkRequest';

interface LandingProps extends RouteComponentProps {}

function LandingComponent({ history }: LandingProps): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const gtmEventTracking = useGtmEventTracking();
  const flow = useFlow();
  const isKeepr = flow === 'Keepr';

  useEffect(() => {
    gtmEventTracking({ event: 'homepage loaded', value: true });
  }, [gtmEventTracking]);

  const active = 'rollover';
  const nonActive = 'those-freaking-boxes';
  const onMouseLeave = () => setHover(null);

  const { user } = useContext(UserContext);

  const handleLogout = () => {
    localStorage.removeItem('user');
    delete axiosKK.defaults.headers.common['Authorization'];
    window.location.assign('/');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      <Helmet>
        <title>{flow === 'St Annes' ? `Student Storage & Shipping | St Anne's College, Oxford` : `Student Storage & Removals | ${flow}`}</title>
        <meta
          name="description"
          content={flow === 'St Annes'
            ? `Student storage and shipping service for St Anne's College, Oxford. Door-to-door box collection, storage and delivery across the UK.`
            : `The #1 Student Storage and Shipping service in the UK. Door-to-door box storage and removals from ${flow}.`}
        />
        <meta property="og:title" content={flow === 'St Annes' ? `Student Storage & Shipping | St Anne's College, Oxford` : `Student Storage & Removals | ${flow}`} />
        <meta property="og:description" content={flow === 'St Annes'
          ? `Student storage and shipping service for St Anne's College, Oxford. Door-to-door box collection, storage and delivery across the UK.`
          : `The #1 Student Storage and Shipping service in the UK. Door-to-door box storage and removals from ${flow}.`} />
        <meta property="og:url" content="https://book.kitkeeper.co.uk/" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      {user ? (
        <div className="landing-auth-container">
          <div className="landing-welcome-user">
            <h2>Welcome back, {user.firstName}!</h2>
            <Link to="/dashboard" className="dashboard-cta">
              Go to Dashboard <ArrowRightIcon className="w-5 h-5" />
            </Link>
            <button onClick={handleLogout} className="logout-link">
               Not you? Logout
            </button>
          </div>
        </div>
      ) : (
        <div className="landing-auth-container">
          <div className="landing-guest-cta">
            <span className="cta-text">Existing customer?</span>
            <div className="cta-actions">
              <Link to="/login" className="btn-login">
                Log In
              </Link>
              <Link to="/register" className="btn-register">
                Register
              </Link>
            </div>
          </div>
        </div>
      )}

      <Title>Let's get you moving...</Title>
      <p className="grey">
        {flow === 'St Annes'
          ? "St Anne's College partner service — expert storage and shipping for Oxford students."
          : `${flow} are experts at storing and shipping boxes all across the UK.`}
      </p>
      <div className="landing-all-box-container">
        <LandingBox
          title="Store Boxes"
          priceTag=""
          description="We provide an all-inclusive service including boxes, packing materials, collection, storage and delivery (one month minimum storage charge)."
          buttonText="Book Storage"
          className={hover === 1 ? active : nonActive}
          onClick={() => {
            history.push('/storage/boxes');
            gtmEventTracking({ event: 'booking_step_option_storage' });
          }}
          staticStateImg="/img/box-icon-large.png"
          rolloverStateImg="/img/box-icon-white-large.png"
          onMouseEnter={() => setHover(1)}
          onMouseLeave={onMouseLeave}
        />
        <LandingBox
          title="Ship Boxes"
          priceTag=""
          description="Our shipping service includes empty boxes, packing materials, collection and delivery of your items to anywhere in the UK."
          buttonText="Ship Boxes"
          className={hover === 2 ? active : nonActive}
          onClick={() => {
            history.push('/shipping/boxes');
            gtmEventTracking({ event: 'booking_step_option_ship' });
          }}
          staticStateImg="/img/ship-icon.png"
          rolloverStateImg="/img/ship-icon-white.png"
          onMouseEnter={() => setHover(2)}
          onMouseLeave={onMouseLeave}
        />
        {isKeepr && (
          <LandingBox
            title="Removals"
            priceTag=""
            description="Our professional removals service includes collection, transportation and delivery of your furniture and belongings."
            buttonText="Book Removal"
            className={hover === 3 ? active : nonActive}
            onClick={() => {
              history.push('/removals');
              gtmEventTracking({ event: 'booking_step_option_removals' });
            }}
            staticStateImg="/img/truck.png"
            rolloverStateImg="/img/truck.png"
            onMouseEnter={() => setHover(3)}
            onMouseLeave={onMouseLeave}
          />
        )}
      </div>
    </div>
  );
}

interface LandingBoxProps {
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  title: string;
  priceTag: string;
  description: string;
  buttonText: string;
  className: string;
  staticStateImg: string;
  rolloverStateImg: string;
}

function LandingBox({
  description,
  priceTag,
  title,
  buttonText,
  staticStateImg,
  rolloverStateImg,
  ...props
}: LandingBoxProps) {
  return (
    <section {...props}>
      <div className="content">
        <h3>{title}</h3>
        <img className="static-state" src={staticStateImg} alt="background" />
        <img
          className="rollover-state"
          src={rolloverStateImg}
          alt="highlighted background"
        />
        <div className="jessieJ-priceTag">{priceTag}</div>
        <p>{description}</p>
      </div>
      <SubmitButton>{buttonText}</SubmitButton>
    </section>
  );
}

export default withRouter(LandingComponent);
